import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FileText, Download, Plus, Calendar, Clock, CheckCircle,
  XCircle, Loader2, Eye, Trash2, RefreshCw, BarChart2,
  TrendingUp, Scale, Building2, Wallet, Receipt, Activity,
  Filter, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useAppData } from '@/context/AppDataContext';
import { useRegion } from '@/context/RegionContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Report catalogue ──────────────────────────────────────────────────────────

function getReportCatalogue(isUS) {
  return [
    {
      id: 'capital_gains',
      label: isUS ? 'Form 8949 — Capital Gains' : 'Schedule CG — Capital Gains',
      description: isUS
        ? 'IRS Form 8949 with STCG & LTCG breakdown, cost basis, and proceeds'
        : 'Schedule CG statement with STCG/LTCG breakdown for ITR filing',
      icon: Scale,
      color: 'bg-[#ffd87a] text-black',
      badge: isUS ? 'Form 8949' : 'Schedule CG',
      tab: 'tax',
      filters: ['financial_year', 'portfolio_id'],
    },
    {
      id: 'portfolio_summary',
      label: 'Portfolio Performance Report',
      description: 'Consolidated portfolio summary with asset allocation, holdings, and P&L',
      icon: BarChart2,
      color: 'bg-[#ffd87a]',
      badge: 'PDF',
      tab: 'portfolio',
      filters: ['portfolio_id'],
    },
    {
      id: 'transaction_history',
      label: 'Transaction History',
      description: 'Complete ledger of all buy/sell transactions with broker details',
      icon: Receipt,
      color: 'bg-[#d9e2c3]',
      badge: 'PDF',
      tab: 'portfolio',
      filters: ['portfolio_id', 'asset_class', 'date_range'],
    },
    {
      id: 'holdings',
      label: 'Holdings Statement',
      description: 'Current portfolio positions with cost basis, current values, and unrealized P&L',
      icon: Wallet,
      color: 'bg-[#d9e2c3]',
      badge: 'PDF',
      tab: 'portfolio',
      filters: ['portfolio_id', 'asset_class'],
    },
    {
      id: 'ic_pack',
      label: 'IC Memo Report',
      description: 'Investment Committee memo report for a specific deal',
      icon: Building2,
      color: 'bg-[#ff3451]',
      badge: 'IC Report',
      tab: 'deals',
      filters: ['deal_id', 'memo_id'],
    },
    {
      id: 'deal_pipeline',
      label: 'Deal Pipeline Summary',
      description: 'Overview of all active deals across pipeline stages with key metrics',
      icon: TrendingUp,
      color: 'bg-[#ffd87a]',
      badge: 'PDF',
      tab: 'deals',
      filters: [],
    },
  ];
}

const ALL_VALUE = '__all__';

const ASSET_CLASSES = [
  { value: ALL_VALUE, label: 'All Asset Classes' },
  { value: 'stocks', label: 'Stocks' },
  { value: 'mf', label: 'Mutual Funds' },
  { value: 'etf', label: 'ETFs' },
  { value: 'bonds', label: 'Bonds' },
  { value: 'fd', label: 'Fixed Deposits' },
  { value: 'gold', label: 'Gold' },
];

const TABS = [
  { id: 'all',       label: 'All Reports' },
  { id: 'portfolio', label: 'Portfolio' },
  { id: 'tax',       label: 'Tax & Gains' },
  { id: 'deals',     label: 'Deals & IC' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function getCurrentFY(isUS) {
  const now = new Date();
  if (isUS) return String(now.getFullYear());
  const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${y}-${String(y + 1).slice(2)}`;
}

function fyOptions(isUS) {
  if (isUS) return ['2026','2025','2024','2023','2022'];
  return ['2025-26','2024-25','2023-24','2022-23','2021-22'];
}

// ── Report Card ───────────────────────────────────────────────────────────────

function ReportCard({ report, onGenerate }) {
  const Icon = report.icon;
  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer group border border-[#e5e5e5]"
          onClick={() => onGenerate(report)}>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl ${report.color} flex items-center justify-center shrink-0 shadow-sm`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-semibold text-[#1a1a1a] text-sm leading-tight">{report.label}</p>
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#f5f5f5] text-[#4a4a4a] shrink-0">{report.badge}</span>
            </div>
            <p className="text-xs text-[#4a4a4a] leading-relaxed">{report.description}</p>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <span className="text-xs text-[#1a1a1a] font-medium group-hover:underline flex items-center gap-1">
            <Download className="w-3.5 h-3.5" /> Generate &amp; Download
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Generate Dialog ───────────────────────────────────────────────────────────

function GenerateDialog({ report, portfolios, deals, icMemos, isUS, onClose }) {
  const [generating, setGenerating] = useState(false);
  const [portfolioId, setPortfolioId] = useState(ALL_VALUE);
  const [assetClass,  setAssetClass]  = useState(ALL_VALUE);
  const [financialYear, setFY]       = useState(() => getCurrentFY(isUS));
  const [dealId,  setDealId]  = useState(ALL_VALUE);
  const [memoId,  setMemoId]  = useState(ALL_VALUE);
  const [startDate, setStart] = useState('');
  const [endDate,   setEnd]   = useState('');

  const hasFilter = (f) => report?.filters?.includes(f);

  const filteredMemos = icMemos.filter(m => dealId === ALL_VALUE || m.deal_id === dealId);

  const handleGenerate = async () => {
    if (!report) return;
    setGenerating(true);
    try {
      let url = '';
      let params = {};
      let filename = '';

      const pId = portfolioId !== ALL_VALUE ? portfolioId : null;
      const aC  = assetClass  !== ALL_VALUE ? assetClass  : null;

      if (report.id === 'capital_gains') {
        url = `${API}/markets/capital-gains/export/pdf`;
        params = { financial_year: financialYear };
        if (pId) params.portfolio_id = pId;
        filename = isUS ? `form_8949_${financialYear}.pdf` : `schedule_cg_${financialYear}.pdf`;
      } else if (report.id === 'portfolio_summary') {
        url = `${API}/markets/portfolios/export/pdf`;
        if (pId) params.portfolio_id = pId;
        filename = 'portfolio_summary.pdf';
      } else if (report.id === 'transaction_history') {
        url = `${API}/markets/transactions/export/pdf`;
        if (pId) params.portfolio_id = pId;
        if (aC) params.asset_class = aC;
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
        filename = 'transaction_history.pdf';
      } else if (report.id === 'holdings') {
        url = `${API}/markets/portfolios/export/pdf`;
        if (pId) params.portfolio_id = pId;
        filename = 'holdings_statement.pdf';
      } else if (report.id === 'ic_pack' || report.id === 'deal_pipeline') {
        if (!memoId || memoId === ALL_VALUE) { toast.error('Please select an IC Memo'); setGenerating(false); return; }
        url = `${API}/deals/ic-memos/${memoId}/report`;
        filename = `ic_memo_report.pdf`;
        params = {};
      } else {
        toast.error('Report type not yet supported');
        setGenerating(false);
        return;
      }

      const res = await axios.get(url, { params, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(href);
      toast.success('Report downloaded');
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Generation failed';
      toast.error(typeof msg === 'string' ? msg : 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  if (!report) return null;
  const Icon = report.icon;

  return (
    <Dialog open={!!report} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg ${report.color} flex items-center justify-center`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            {report.label}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {hasFilter('financial_year') && (
            <div>
              <label className="text-xs font-medium text-[#4a4a4a] mb-1.5 block">
                {isUS ? 'Tax Year' : 'Financial Year'}
              </label>
              <Select value={financialYear} onValueChange={setFY}>
                <SelectTrigger className="text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {fyOptions(isUS).map(y => (
                    <SelectItem key={y} value={y}>{isUS ? `Tax Year ${y}` : `FY ${y}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {hasFilter('portfolio_id') && portfolios.length > 0 && (
            <div>
              <label className="text-xs font-medium text-[#4a4a4a] mb-1.5 block">Portfolio (optional)</label>
              <Select value={portfolioId} onValueChange={setPortfolioId}>
                <SelectTrigger className="text-sm"><SelectValue placeholder="All portfolios" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_VALUE}>All portfolios</SelectItem>
                  {portfolios.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {hasFilter('asset_class') && (
            <div>
              <label className="text-xs font-medium text-[#4a4a4a] mb-1.5 block">Asset Class (optional)</label>
              <Select value={assetClass} onValueChange={setAssetClass}>
                <SelectTrigger className="text-sm"><SelectValue placeholder="All asset classes" /></SelectTrigger>
                <SelectContent>
                  {ASSET_CLASSES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {hasFilter('date_range') && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1.5 block">From Date</label>
                <input type="date" value={startDate} onChange={e => setStart(e.target.value)}
                  className="w-full border border-[#e5e5e5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffd87a]" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1.5 block">To Date</label>
                <input type="date" value={endDate} onChange={e => setEnd(e.target.value)}
                  className="w-full border border-[#e5e5e5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#ffd87a]" />
              </div>
            </div>
          )}

          {(report.id === 'ic_pack' || report.id === 'deal_pipeline') && (
            <>
              {deals.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-[#4a4a4a] mb-1.5 block">Deal</label>
                  <Select value={dealId} onValueChange={v => { setDealId(v); setMemoId(ALL_VALUE); }}>
                    <SelectTrigger className="text-sm"><SelectValue placeholder="Select deal" /></SelectTrigger>
                    <SelectContent>
                      {deals.map(d => <SelectItem key={d.id} value={d.id}>{d.deal_name} — {d.company_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-[#4a4a4a] mb-1.5 block">IC Memo</label>
                <Select value={memoId} onValueChange={setMemoId}>
                  <SelectTrigger className="text-sm"><SelectValue placeholder="Select IC memo" /></SelectTrigger>
                  <SelectContent>
                    {filteredMemos.length > 0
                      ? filteredMemos.map(m => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.title || `Memo ${m.id.slice(0,8)}`}
                            {m.status === 'approved' ? ' ✓ Approved' : ` (${m.status})`}
                          </SelectItem>
                        ))
                      : <SelectItem value="__none__" disabled>No memos found</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleGenerate} disabled={generating}
            className={`${report.color} text-white hover:opacity-90 border-0`}>
            {generating
              ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Generating…</>
              : <><Download className="w-4 h-4 mr-1.5" />Generate &amp; Download</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function ReportsPage() {
  const { user } = useAuth();
  const region = useRegion();
  const isUS = region?.isUS || false;

  const [activeTab, setActiveTab] = useState('all');
  const [portfolios, setPortfolios] = useState([]);
  const [deals, setDeals] = useState([]);
  const [icMemos, setIcMemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);

  const catalogue = getReportCatalogue(isUS);
  const visibleReports = activeTab === 'all'
    ? catalogue
    : catalogue.filter(r => r.tab === activeTab);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [portRes, dealRes, memoRes] = await Promise.allSettled([
        axios.get(`${API}/markets/portfolios`),
        axios.get(`${API}/deals/`),
        axios.get(`${API}/deals/ic-memos`),
      ]);
      if (portRes.status === 'fulfilled') setPortfolios(portRes.value.data || []);
      if (dealRes.status === 'fulfilled') setDeals(dealRes.value.data || []);
      if (memoRes.status === 'fulfilled') setIcMemos(memoRes.value.data || []);
    } catch {
      // non-fatal
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#dae4c4] flex items-center justify-center shadow-sm">
            <FileText className="w-5 h-5 text-[#1a1a1a]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#1a1a1a]">Reports</h1>
            <p className="text-sm text-[#4a4a4a] mt-0.5">
              Generate and download portfolio, tax, and deal reports
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} className="text-[#4a4a4a]">
          <RefreshCw className="w-4 h-4 mr-1.5" />
          Refresh
        </Button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#e5e5e5] rounded-xl p-4">
          <p className="text-xs text-[#4a4a4a] mb-1">Portfolios</p>
          <p className="text-2xl font-bold text-[#1a1a1a]">{portfolios.length}</p>
        </div>
        <div className="bg-white border border-[#e5e5e5] rounded-xl p-4">
          <p className="text-xs text-[#4a4a4a] mb-1">Active Deals</p>
          <p className="text-2xl font-bold text-[#1a1a1a]">{deals.length}</p>
        </div>
        <div className="bg-white border border-[#e5e5e5] rounded-xl p-4">
          <p className="text-xs text-[#4a4a4a] mb-1">IC Memos</p>
          <p className="text-2xl font-bold text-[#1a1a1a]">{icMemos.length}</p>
        </div>
        <div className="bg-white border border-[#e5e5e5] rounded-xl p-4">
          <p className="text-xs text-[#4a4a4a] mb-1">Report Types</p>
          <p className="text-2xl font-bold text-[#1a1a1a]">{catalogue.length}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="inline-flex gap-1 bg-[#f5f5f5] border border-[#e5e5e5] p-1 rounded-lg">
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-[#ffd87a] text-black'
                : 'text-[#4a4a4a] hover:text-[#1a1a1a] hover:bg-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Report cards grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => (
            <div key={i} className="h-36 bg-[#f5f5f5] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleReports.map(r => (
            <ReportCard key={r.id} report={r} onGenerate={setSelectedReport} />
          ))}
        </div>
      )}

      {/* Note */}
      <div className="bg-[#f9f9f9] border border-[#e5e5e5] rounded-xl p-4 text-sm text-[#1a1a1a]">
        <strong>PDF reports</strong> are generated on-demand using your live data. Select a report above, configure filters, and download instantly. No data is stored — each download reflects the current state of your portfolio.
      </div>

      {/* Generate dialog */}
      <GenerateDialog
        report={selectedReport}
        portfolios={portfolios}
        deals={deals}
        icMemos={icMemos}
        isUS={isUS}
        onClose={() => setSelectedReport(null)}
      />
    </div>
  );
}
